import { useEffect } from 'react';

/**
 * Componente SEO - Gerencia meta tags e structured data
 * 
 * Uso:
 * <SEO 
 *   title="Título da Página"
 *   description="Descrição da página"
 *   keywords="palavras, chave, separadas"
 *   canonical="https://www.informezz.com.br"
 * />
 */
const SEO = ({
  title = 'Informezz - Sistema de Gestão para Lojas de Moda',
  description = 'Sistema completo de gestão (ERP) para lojas de roupas, calçados e acessórios. Mais de 20 anos de experiência em soluções tecnológicas para o varejo de moda. Gestão de condicionais, PDV, estoque, financeiro e muito mais.',
  keywords = 'sistema gestão loja moda, erp varejo roupas, software loja calçados, gestão condicionais, pdv loja roupas, sistema venda roupas, erp nuvem moda, software varejo moda, gestão estoque roupas, sistema financeiro loja',
  canonical = 'https://www.informezz.com.br',
  ogImage = 'https://www.informezz.com.br/og-image.jpg',
  ogType = 'website',
}) => {
  
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (property, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Informezz Sistemas');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('googlebot', 'index, follow');

    // Open Graph meta tags (Facebook, LinkedIn)
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonical, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:site_name', 'Informezz Sistemas', true);
    updateMetaTag('og:locale', 'pt_BR', true);

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Add structured data (JSON-LD)
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Informezz Sistemas',
      'description': description,
      'url': 'https://www.informezz.com.br',
      'logo': 'https://www.informezz.com.br/logo.png',
      'telephone': '+55-46-3225-0131',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Rua Xingu, 61',
        'addressLocality': 'Pato Branco',
        'addressRegion': 'PR',
        'postalCode': '85501-000',
        'addressCountry': 'BR'
      },
      'contactPoint': [
        {
          '@type': 'ContactPoint',
          'telephone': '+55-46-3225-0131',
          'contactType': 'Customer Service',
          'areaServed': 'BR',
          'availableLanguage': 'Portuguese'
        },
        {
          '@type': 'ContactPoint',
          'telephone': '+55-46-99110-7619',
          'contactType': 'Sales',
          'areaServed': 'BR',
          'availableLanguage': 'Portuguese'
        }
      ],
      'sameAs': [
        'https://www.instagram.com/Informezz',
        'https://www.facebook.com/people/Informezz-Sistemas/61557268662997/',
        'https://www.youtube.com/@informezz'
      ],
      'foundingDate': '2000',
      'numberOfEmployees': {
        '@type': 'QuantitativeValue',
        'value': '50'
      },
      'areaServed': {
        '@type': 'Country',
        'name': 'Brazil'
      }
    };

    // Add Software Application structured data
    const softwareData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'Informezz ERP',
      'applicationCategory': 'BusinessApplication',
      'operatingSystem': 'Web, Android, iOS',
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'BRL',
        'price': '0',
        'description': 'Fale com um especialista para conhecer a solução'
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.8',
        'ratingCount': '500'
      }
    };

    // Insert or update structured data scripts
    let structuredDataScript = document.getElementById('structured-data-org');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.id = 'structured-data-org';
      structuredDataScript.type = 'application/ld+json';
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(structuredData);

    let softwareDataScript = document.getElementById('structured-data-software');
    if (!softwareDataScript) {
      softwareDataScript = document.createElement('script');
      softwareDataScript.id = 'structured-data-software';
      softwareDataScript.type = 'application/ld+json';
      document.head.appendChild(softwareDataScript);
    }
    softwareDataScript.textContent = JSON.stringify(softwareData);

  }, [title, description, keywords, canonical, ogImage, ogType]);

  return null; // This component doesn't render anything
};

export default SEO;
